/*───────────────────────────────────────────────────────────────────────────*\
  File      : src/utils/userDataApi.ts
  Purpose   : Client-side helper that wraps every /api/user_data/* endpoint in
              type-safe Promise functions for UI developers.; Each function
              returns the parsed JSON response without throwing for HTTP
              errors so callers can rely on the `{ success, error }` pattern.;
\*───────────────────────────────────────────────────────────────────────────*/

import {
  /* core save */
  SaveCanvasRequest,
  SaveCanvasResponse,
  SaveProjectRequest,
  SaveProjectResponse,
  /* wizard start */
  StartCanvasGenerationWizardRequest,
  StartCanvasGenerationWizardResponse,
  StartScratchConceptWizardRequest,
  StartScratchConceptWizardResponse,
  StartCanvasConceptWizardRequest,
  StartCanvasConceptWizardResponse,
  /* append */
  AppendGenerationImagesRequest,
  AppendGenerationImagesResponse,
  /* listings */
  ListCanvasesRequest,
  ListCanvasesResponse,
  ListProjectsRequest,
  ListProjectsResponse,
  /* detail */
  GetCanvasRequest,
  GetCanvasResponse,
  GetProjectRequest,
  GetProjectResponse,
  /* rename */
  RenameResponse,
  /* recycle-bin */
  ListDeletedRequest,
  ListDeletedResponse,
  RestoreRequest,
  RestoreResponse,
  /* JSON update */
  UpdateProjectJsonRequest,
  UpdateProjectJsonResponse,
  /* favorites */
  AddFavoriteRequest,
  AddFavoriteResponse,
  RemoveFavoriteRequest,
  RemoveFavoriteResponse,
  ListFavoritesRequest,
  ListFavoritesResponse,
  /* wizard images */
  ListWizardImagesRequest,
  ListWizardImagesResponse,
  /* populate details */
  PopulateProjectDetailsRequest,
  PopulateProjectDetailsResponse,
} from "@/src/types/user-data";

/*===========================================================================
  ▸ Internal helper                                                         
===========================================================================*/

/**
 * POST helper that always includes credentials and JSON headers.;
 */
async function post<T>(path: string, payload: object): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return (await res.json()) as T;
}

/*===========================================================================
  ▸ Core save endpoints                                                     
===========================================================================*/

export function saveCanvas(payload: SaveCanvasRequest): Promise<SaveCanvasResponse> {
  return post("/api/user_data/save-canvas", payload);
}

export function saveProject(payload: SaveProjectRequest): Promise<SaveProjectResponse> {
  return post("/api/user_data/save-project", payload);
}

/*===========================================================================
  ▸ Wizard session start                                                     
===========================================================================*/

/**
 * Begin a new canvas-generation wizard session.; Server supplies ids/counts.;
 */
export function startCanvasGenerationWizard(
  payload: StartCanvasGenerationWizardRequest,
): Promise<StartCanvasGenerationWizardResponse> {
  return post("/api/user_data/start-canvas-wizard", payload);
}

/**
 * Begin a new scratch-concept wizard session.;;
 */
export function startScratchConceptWizard(
  payload: StartScratchConceptWizardRequest,
): Promise<StartScratchConceptWizardResponse> {
  return post("/api/user_data/start-scratch-wizard", payload);
}

/**
 * Begin a new canvas-concept wizard session.;;
 */
export function startCanvasConceptWizard(
  payload: StartCanvasConceptWizardRequest,
): Promise<StartCanvasConceptWizardResponse> {
  return post("/api/user_data/start-canvas-concept-wizard", payload);
}

/*===========================================================================
  ▸ Append generation images                                                
===========================================================================*/

export function appendGenerationImages(
  payload: AppendGenerationImagesRequest,
): Promise<AppendGenerationImagesResponse> {
  return post("/api/user_data/append-generation", payload);
}

/*===========================================================================
  ▸ Listings                                                                
===========================================================================*/

export function listCanvases(
  payload: ListCanvasesRequest,
): Promise<ListCanvasesResponse> {
  return post("/api/user_data/list-canvases", payload);
}

export function listProjects(
  payload: ListProjectsRequest,
): Promise<ListProjectsResponse> {
  return post("/api/user_data/list-projects", payload);
}

/**
 * List **all wizard images** generated by the user across every wizard type,
 * with server-side filtering, pagination, and sorting.;
 */
export function listWizardImages(
  payload: ListWizardImagesRequest,
): Promise<ListWizardImagesResponse> {
  return post("/api/user_data/list-wizard-images", payload);
}

/*===========================================================================
  ▸ Detail fetch                                                            
===========================================================================*/

export function getCanvas(payload: GetCanvasRequest): Promise<GetCanvasResponse> {
  return post("/api/user_data/get-canvas", payload);
}

export function getProject(
  payload: GetProjectRequest,
): Promise<GetProjectResponse> {
  return post("/api/user_data/get-project", payload);
}

/*===========================================================================
  ▸ Rename                                                                  
===========================================================================*/

export function renameCanvas(
  canvasId: string,
  newName: string,
): Promise<RenameResponse> {
  return post("/api/user_data/rename", {
    type: "canvas",
    id: canvasId,
    newName,
  });
}

export function renameProject(
  projectId: string,
  kind: "scratch" | "canvas",
  newName: string,
): Promise<RenameResponse> {
  return post("/api/user_data/rename", {
    type: "project",
    id: projectId,
    kind,
    newName,
  });
}

/*===========================================================================
  ▸ Soft-delete & recycle bin                                               
===========================================================================*/

export function deleteCanvas(
  canvasId: string,
): Promise<{ success: boolean; error?: string }> {
  return post("/api/user_data/delete", { type: "canvas", id: canvasId });
}

export function deleteProject(
  projectId: string,
  kind: "scratch" | "canvas",
): Promise<{ success: boolean; error?: string }> {
  return post("/api/user_data/delete", { type: "project", id: projectId, kind });
}

export function listDeletedCanvases(
  userId: string,
): Promise<ListDeletedResponse> {
  return post("/api/user_data/list-deleted", {
    userId,
    type: "canvas",
  } as ListDeletedRequest);
}

export function listDeletedProjects(
  userId: string,
): Promise<ListDeletedResponse> {
  return post("/api/user_data/list-deleted", {
    userId,
    type: "project",
  } as ListDeletedRequest);
}

export function restoreCanvas(canvasId: string): Promise<RestoreResponse> {
  return post("/api/user_data/restore", {
    type: "canvas",
    id: canvasId,
  } as RestoreRequest);
}

export function restoreProject(
  projectId: string,
  kind: "scratch" | "canvas",
): Promise<RestoreResponse> {
  return post("/api/user_data/restore", {
    type: "project",
    id: projectId,
    kind,
  } as RestoreRequest);
}

/*===========================================================================
  ▸ Project JSON-blob update                                                
===========================================================================*/

export function updateProjectJson(
  payload: UpdateProjectJsonRequest,
): Promise<UpdateProjectJsonResponse> {
  return post("/api/user_data/update-project-json", payload);
}

/*===========================================================================
  ▸ Favorites                                                               
===========================================================================*/

export function addFavorite(
  payload: AddFavoriteRequest,
): Promise<AddFavoriteResponse> {
  return post("/api/user_data/add-favorite", payload);
}

export function removeFavorite(
  payload: RemoveFavoriteRequest,
): Promise<RemoveFavoriteResponse> {
  return post("/api/user_data/remove-favorite", payload);
}

export function listFavorites(
  payload: ListFavoritesRequest,
): Promise<ListFavoritesResponse> {
  return post("/api/user_data/list-favorites", payload);
}

/*===========================================================================
  ▸ Populate project details                                                 
===========================================================================*/

/**
 * Populate any missing project details (summary, itemization, palette, plan).;
 * The server verifies ownership and fills only details that are effectively empty.;
 */
export function populateProjectDetails(
  payload: PopulateProjectDetailsRequest,
): Promise<PopulateProjectDetailsResponse> {
  return post("/api/user_data/populate-project-details", payload);
}

/*──────────────────────────── End of file ────────────────────────────────*/
